import React, { useState } from 'react';
import {
    EvaluationArea, EvaluationParameter, EvaluationIndicatorType,
    AutomaticIndicatorCode, SubcontractorEvaluationConfig, Profile,
    EvaluationRange, EvaluationRangeOperator, VisualDistinctionType,
    VisualDistinctionLevel
} from '../../types';

interface EvaluationConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    config: SubcontractorEvaluationConfig;
    onSaveConfig: (updatedConfig: SubcontractorEvaluationConfig) => void;
    globalProfiles?: Profile[];
    hasEditPermission?: boolean;
}

export const AUTOMATIC_INDICATOR_OPTIONS: { code: AutomaticIndicatorCode; label: string; desc: string }[] = [
    {
        code: 'PPC',
        label: '1. PPC (Porcentaje del Plan Cumplido)',
        desc: 'Cantidad de tareas cumplidas / Total de tareas asignadas en la planificación semanal.'
    },
    {
        code: 'CNC',
        label: '2. C.N.C (Causas de No Cumplimiento)',
        desc: 'Cantidad de C.N.C con rangos: 0 = 100%, 1 a 5 = 70%, 6 a 10 = 50%, >10 = 10%.'
    },
    {
        code: 'CONFIABILIDAD',
        label: '3. Confiabilidad de Calidad',
        desc: 'Cantidad de protocolos rechazados / Cantidad de protocolos activos en control.'
    },
    {
        code: 'PCR',
        label: '4. PCR (Porcentaje de Restricciones Liberadas)',
        desc: 'Cantidad de restricciones liberadas / Cantidad total de restricciones asignadas.'
    }
];

export const RANGE_OPERATOR_OPTIONS: { value: EvaluationRangeOperator; label: string; symbol: string }[] = [
    { value: '>=', label: 'Mayor o igual que (>=)', symbol: '>=' },
    { value: '>', label: 'Mayor que (>)', symbol: '>' },
    { value: 'between', label: 'Entre valores', symbol: 'Entre' },
    { value: '<=', label: 'Menor o igual que (<=)', symbol: '<=' },
    { value: '<', label: 'Menor que (<)', symbol: '<' },
    { value: '=', label: 'Igual (=)', symbol: '=' }
];

export const VISUAL_SETS: { type: VisualDistinctionType; label: string; icon: string }[] = [
    { type: 'carita', label: 'Caritas', icon: 'sentiment_satisfied' },
    { type: 'semaforo', label: 'Semáforo', icon: 'traffic' },
    { type: 'flecha', label: 'Flechas', icon: 'trending_up' }
];

export const DISTINCTION_LEVELS: {
    level: VisualDistinctionLevel;
    label: string;
    color: string;
    icons: Record<VisualDistinctionType, string>;
}[] = [
    {
        level: 'Excelente',
        label: 'Excelente (Verde fuerte)',
        color: '#16a34a',
        icons: {
            carita: 'sentiment_very_satisfied',
            semaforo: 'circle',
            flecha: 'arrow_upward'
        }
    },
    {
        level: 'Bueno',
        label: 'Bueno (Verde opaco)',
        color: '#84cc16',
        icons: {
            carita: 'sentiment_satisfied',
            semaforo: 'circle',
            flecha: 'north_east'
        }
    },
    {
        level: 'Regular',
        label: 'Regular (Amarillo)',
        color: '#eab308',
        icons: {
            carita: 'sentiment_neutral',
            semaforo: 'circle',
            flecha: 'trending_flat'
        }
    },
    {
        level: 'Alerta',
        label: 'Alerta (Naranjo)',
        color: '#f97316',
        icons: {
            carita: 'sentiment_dissatisfied',
            semaforo: 'circle',
            flecha: 'south_east'
        }
    },
    {
        level: 'Deficiente',
        label: 'Deficiente (Rojo)',
        color: '#ef4444',
        icons: {
            carita: 'sentiment_very_dissatisfied',
            semaforo: 'circle',
            flecha: 'arrow_downward'
        }
    }
];

export const DEFAULT_EVALUATION_RANGES: EvaluationRange[] = [
    {
        id: 'range-1',
        name: 'Sobresaliente',
        operator: '>=',
        value: 90,
        visualType: 'carita',
        level: 'Excelente',
        color: '#16a34a',
        icon: 'sentiment_very_satisfied'
    },
    {
        id: 'range-2',
        name: 'Bueno',
        operator: 'between',
        value: 80,
        minValue: 80,
        maxValue: 89.9,
        visualType: 'carita',
        level: 'Bueno',
        color: '#84cc16',
        icon: 'sentiment_satisfied'
    },
    {
        id: 'range-3',
        name: 'Aceptable',
        operator: 'between',
        value: 70,
        minValue: 70,
        maxValue: 79.9,
        visualType: 'carita',
        level: 'Regular',
        color: '#eab308',
        icon: 'sentiment_neutral'
    },
    {
        id: 'range-4',
        name: 'Alerta',
        operator: 'between',
        value: 50,
        minValue: 50,
        maxValue: 69.9,
        visualType: 'carita',
        level: 'Alerta',
        color: '#f97316',
        icon: 'sentiment_dissatisfied'
    },
    {
        id: 'range-5',
        name: 'Deficiente',
        operator: '<',
        value: 50,
        visualType: 'carita',
        level: 'Deficiente',
        color: '#ef4444',
        icon: 'sentiment_very_dissatisfied'
    }
];

export const getEvaluationRangeForScore = (
    score: number,
    ranges?: EvaluationRange[]
): EvaluationRange | null => {
    const list = ranges && ranges.length > 0 ? ranges : DEFAULT_EVALUATION_RANGES;
    for (const r of list) {
        if (r.operator === '>') {
            if (score > r.value) return r;
        } else if (r.operator === '>=') {
            if (score >= r.value) return r;
        } else if (r.operator === '=') {
            if (Math.abs(score - r.value) < 0.001) return r;
        } else if (r.operator === '<') {
            if (score < r.value) return r;
        } else if (r.operator === '<=') {
            if (score <= r.value) return r;
        } else if (r.operator === 'between') {
            const min = r.minValue !== undefined ? r.minValue : 0;
            const max = r.maxValue !== undefined ? r.maxValue : 100;
            if (score >= min && score <= max) return r;
        }
    }
    return null;
};

export const EvaluationRangeBadge: React.FC<{
    score?: number;
    range?: EvaluationRange | null;
    ranges?: EvaluationRange[];
    showDistinction?: boolean;
    showScore?: boolean;
    className?: string;
}> = ({ score, range, ranges, showDistinction = true, showScore = false, className = '' }) => {
    const matchedRange = range || (score !== undefined ? getEvaluationRangeForScore(score, ranges) : null);
    
    if (!matchedRange) {
        if (score !== undefined && showScore) {
            return <span className={`text-xs font-bold text-slate-600 dark:text-slate-300 ${className}`}>{score}%</span>;
        }
        return null;
    }

    const { visualType, level = 'Excelente', color, icon } = matchedRange;
    const distinctionInfo = DISTINCTION_LEVELS.find(d => d.level === level) || DISTINCTION_LEVELS[0];
    const finalIcon = icon || distinctionInfo.icons[visualType] || 'circle';
    const finalColor = color || distinctionInfo.color;

    return (
        <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-xs transition-all ${className}`}
            style={{
                backgroundColor: `${finalColor}18`,
                borderColor: `${finalColor}45`,
                color: finalColor
            }}
            title={`${level} ${score !== undefined ? `(${score}%)` : ''}`}
        >
            {visualType === 'semaforo' ? (
                <span className="material-symbols-outlined text-[14px] leading-none shrink-0" style={{ color: finalColor }}>circle</span>
            ) : (
                <span className="material-symbols-outlined text-[17px] leading-none shrink-0">{finalIcon}</span>
            )}
            {showScore && score !== undefined && <span className="font-extrabold">{score}%</span>}
            {showDistinction && <span className="font-extrabold">{level}</span>}
        </span>
    );
};

export const EvaluationConfigModal: React.FC<EvaluationConfigModalProps> = ({
    isOpen,
    onClose,
    config,
    onSaveConfig,
    globalProfiles = [],
    hasEditPermission = true
}) => {
    // Tabs state
    const [activeTab, setActiveTab] = useState<'evaluacion' | 'rangos'>('evaluacion');

    // Working local copy of areas
    const [areas, setAreas] = useState<EvaluationArea[]>(() => config.areas || []);

    // Working local copy of ranges
    const [ranges, setRanges] = useState<EvaluationRange[]>(() => {
        if (config.ranges && config.ranges.length > 0) {
            // Asegurar que cada rango tenga level válido
            return config.ranges.map(r => {
                const matchedLevel = DISTINCTION_LEVELS.find(d => d.level === r.level) || DISTINCTION_LEVELS.find(d => d.color === r.color) || DISTINCTION_LEVELS[0];
                return {
                    ...r,
                    visualType: (r.visualType === 'carita' || r.visualType === 'semaforo' || r.visualType === 'flecha') ? r.visualType : 'carita',
                    level: matchedLevel.level,
                    color: matchedLevel.color,
                    icon: matchedLevel.icons[r.visualType as VisualDistinctionType] || matchedLevel.icons.carita
                };
            });
        }
        return DEFAULT_EVALUATION_RANGES;
    });

    // Area Modal
    const [areaModalOpen, setAreaModalOpen] = useState(false);
    const [editingAreaId, setEditingAreaId] = useState<string | null>(null);
    const [areaForm, setAreaForm] = useState<{ name: string; weight: string }>({ name: '', weight: '' });

    // Parameter Modal
    const [paramModalOpen, setParamModalOpen] = useState(false);
    const [targetAreaIdForParam, setTargetAreaIdForParam] = useState<string | null>(null);
    const [editingParamId, setEditingParamId] = useState<string | null>(null);
    const [paramForm, setParamForm] = useState<{
        name: string;
        weight: string;
        profileId: string;
        indicatorDescription: string;
        indicatorType: EvaluationIndicatorType;
        automaticCode: AutomaticIndicatorCode;
    }>({
        name: '',
        weight: '',
        profileId: '',
        indicatorDescription: '',
        indicatorType: 'Manual',
        automaticCode: 'PPC'
    });

    if (!isOpen) return null;

    // Calculate total weights for Areas
    const totalAreaWeight = areas.reduce((sum, a) => sum + (Number(a.weight) || 0), 0);

    // --- Area Actions ---
    const handleOpenAddArea = () => {
        setEditingAreaId(null);
        setAreaForm({ name: '', weight: '' });
        setAreaModalOpen(true);
    };

    const handleOpenEditArea = (area: EvaluationArea) => {
        setEditingAreaId(area.id);
        setAreaForm({ name: area.name, weight: String(area.weight) });
        setAreaModalOpen(true);
    };

    const handleSaveArea = () => {
        if (!areaForm.name.trim()) {
            alert('Por favor ingrese el Nombre del Área');
            return;
        }
        const weightNum = parseFloat(areaForm.weight) || 0;
        if (weightNum <= 0) {
            alert('El ponderador debe ser mayor a 0%');
            return;
        }

        setAreas(prev => {
            if (editingAreaId) {
                return prev.map(a => a.id === editingAreaId ? { ...a, name: areaForm.name.trim(), weight: weightNum } : a);
            } else {
                const nextCorrelative = prev.length + 1;
                const newArea: EvaluationArea = {
                    id: `area-${Date.now()}`,
                    correlative: nextCorrelative,
                    name: areaForm.name.trim(),
                    weight: weightNum,
                    parameters: []
                };
                return [...prev, newArea];
            }
        });
        setAreaModalOpen(false);
    };

    const handleDeleteArea = (areaId: string) => {
        if (!confirm('¿Está seguro de eliminar esta Área y todos sus parámetros?')) return;
        setAreas(prev => {
            const filtered = prev.filter(a => a.id !== areaId);
            return filtered.map((a, idx) => ({
                ...a,
                correlative: idx + 1
            }));
        });
    };

    // --- Parameter Actions ---
    const handleOpenAddParam = (areaId: string) => {
        setTargetAreaIdForParam(areaId);
        setEditingParamId(null);
        setParamForm({
            name: '',
            weight: '',
            profileId: globalProfiles.filter(p => !p.isDeleted)[0]?.id || '',
            indicatorDescription: '',
            indicatorType: 'Manual',
            automaticCode: 'PPC'
        });
        setParamModalOpen(true);
    };

    const handleOpenEditParam = (areaId: string, param: EvaluationParameter) => {
        setTargetAreaIdForParam(areaId);
        setEditingParamId(param.id);
        setParamForm({
            name: param.name,
            weight: String(param.weight),
            profileId: param.profileId || '',
            indicatorDescription: param.indicatorDescription,
            indicatorType: param.indicatorType,
            automaticCode: param.automaticCode || 'PPC'
        });
        setParamModalOpen(true);
    };

    const handleSaveParam = () => {
        if (!paramForm.name.trim()) {
            alert('Por favor ingrese el Nombre del Parámetro');
            return;
        }
        const weightNum = parseFloat(paramForm.weight) || 0;
        if (weightNum <= 0) {
            alert('El ponderador del parámetro debe ser mayor a 0%');
            return;
        }

        const selectedProfile = globalProfiles.find(p => p.id === paramForm.profileId);

        setAreas(prev => prev.map(area => {
            if (area.id !== targetAreaIdForParam) return area;

            let updatedParams = [...area.parameters];
            if (editingParamId) {
                updatedParams = updatedParams.map(p => {
                    if (p.id === editingParamId) {
                        return {
                            ...p,
                            name: paramForm.name.trim(),
                            weight: weightNum,
                            profileId: paramForm.profileId,
                            profileName: selectedProfile?.name || '',
                            indicatorDescription: paramForm.indicatorDescription.trim(),
                            indicatorType: paramForm.indicatorType,
                            automaticCode: paramForm.indicatorType === 'Automatico' ? paramForm.automaticCode : undefined
                        };
                    }
                    return p;
                });
            } else {
                const nextCorrelative = updatedParams.length + 1;
                const newParam: EvaluationParameter = {
                    id: `param-${Date.now()}`,
                    correlative: nextCorrelative,
                    name: paramForm.name.trim(),
                    weight: weightNum,
                    profileId: paramForm.profileId,
                    profileName: selectedProfile?.name || '',
                    indicatorDescription: paramForm.indicatorDescription.trim(),
                    indicatorType: paramForm.indicatorType,
                    automaticCode: paramForm.indicatorType === 'Automatico' ? paramForm.automaticCode : undefined
                };
                updatedParams.push(newParam);
            }

            return {
                ...area,
                parameters: updatedParams.map((p, idx) => ({ ...p, correlative: idx + 1 }))
            };
        }));

        setParamModalOpen(false);
    };

    const handleDeleteParam = (areaId: string, paramId: string) => {
        if (!confirm('¿Está seguro de eliminar este Parámetro?')) return;
        setAreas(prev => prev.map(area => {
            if (area.id !== areaId) return area;
            const filtered = area.parameters.filter(p => p.id !== paramId);
            return {
                ...area,
                parameters: filtered.map((p, idx) => ({ ...p, correlative: idx + 1 }))
            };
        }));
    };

    // --- Range Actions ---
    const handleAddRange = () => {
        const newRange: EvaluationRange = {
            id: `range-${Date.now()}`,
            name: `Nuevo Rango ${ranges.length + 1}`,
            operator: '>=',
            value: 80,
            visualType: 'carita',
            level: 'Bueno',
            color: '#84cc16',
            icon: 'sentiment_satisfied'
        };
        setRanges(prev => [...prev, newRange]);
    };

    const handleUpdateRange = (rangeId: string, patch: Partial<EvaluationRange>) => {
        setRanges(prev => prev.map(r => {
            if (r.id !== rangeId) return r;
            const updated = { ...r, ...patch };
            
            // Sincronizar level, color e icon si cambia visualType o level
            const activeType = updated.visualType || 'carita';
            const activeLevel = updated.level || 'Excelente';
            const distinctionInfo = DISTINCTION_LEVELS.find(d => d.level === activeLevel) || DISTINCTION_LEVELS[0];

            updated.color = distinctionInfo.color;
            updated.icon = distinctionInfo.icons[activeType];

            return updated;
        }));
    };

    const handleDeleteRange = (rangeId: string) => {
        if (ranges.length <= 1) {
            alert('Debe existir al menos un rango de evaluación configurado.');
            return;
        }
        setRanges(prev => prev.filter(r => r.id !== rangeId));
    };

    const handleMoveRange = (index: number, direction: 'up' | 'down') => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === ranges.length - 1) return;
        
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        setRanges(prev => {
            const next = [...prev];
            const temp = next[index];
            next[index] = next[targetIndex];
            next[targetIndex] = temp;
            return next;
        });
    };

    const handleResetDefaultRanges = () => {
        if (confirm('¿Deseas restablecer los rangos a los 5 niveles estándar por defecto?')) {
            setRanges(DEFAULT_EVALUATION_RANGES);
        }
    };

    // Save full config
    const handleSaveGlobalConfig = () => {
        onSaveConfig({
            ...config,
            areas,
            ranges,
            updatedAt: new Date().toISOString()
        });
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden">
                
                {/* Main Header */}
                <div className="px-5 py-3.5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/90 dark:bg-slate-800/80 shrink-0 gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 shadow-xs">
                            <span className="material-symbols-outlined text-[24px]">settings_suggest</span>
                        </div>
                        <div>
                            <h3 className="text-sm md:text-base font-black text-slate-800 dark:text-white leading-tight">
                                Configuración de Evaluación de SC
                            </h3>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                Gestiona áreas, parámetros ponderados y umbrales con distinción visual para subcontratistas.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Tab Switcher */}
                        <div className="flex items-center gap-1 bg-slate-200/80 dark:bg-slate-800 p-1 rounded-xl border border-slate-300/60 dark:border-slate-700">
                            <button
                                onClick={() => setActiveTab('evaluacion')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                    activeTab === 'evaluacion'
                                        ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[16px]">fact_check</span>
                                Evaluación
                            </button>
                            <button
                                onClick={() => setActiveTab('rangos')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                    activeTab === 'rangos'
                                        ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[16px]">tune</span>
                                Rangos
                            </button>
                        </div>

                        {/* Action buttons based on active tab */}
                        {hasEditPermission && activeTab === 'evaluacion' && (
                            <button
                                onClick={handleOpenAddArea}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-xs font-bold rounded-xl shadow-md transition-all cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                <span className="hidden sm:inline">Añadir Área</span>
                            </button>
                        )}

                        {hasEditPermission && activeTab === 'rangos' && (
                            <button
                                onClick={handleAddRange}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-xs font-bold rounded-xl shadow-md transition-all cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                <span className="hidden sm:inline">+ Rango</span>
                            </button>
                        )}

                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                            title="Cerrar ventana"
                        >
                            <span className="material-symbols-outlined text-[20px]">close</span>
                        </button>
                    </div>
                </div>

                {/* Sub-Header / Status Bar */}
                {activeTab === 'evaluacion' ? (
                    <div className="px-6 py-2 bg-slate-100 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs shrink-0">
                        <div className="flex items-center gap-2">
                            <span className="text-slate-500 font-medium">Ponderación Total de Áreas:</span>
                            <span className={`font-black px-2 py-0.5 rounded-full text-xs ${
                                totalAreaWeight === 100 
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300' 
                                    : 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300'
                            }`}>
                                {totalAreaWeight}% {totalAreaWeight === 100 ? '(Correcto 100%)' : '(Recomendado: 100%)'}
                            </span>
                        </div>
                        <span className="text-slate-400 text-[11px]">
                            Total de Áreas: <strong className="text-slate-700 dark:text-slate-200">{areas.length}</strong>
                        </span>
                    </div>
                ) : (
                    <div className="px-6 py-2 bg-slate-100 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs shrink-0">
                        <div className="flex items-center gap-2">
                            <span className="text-slate-500 font-medium">Umbrales e Indicadores:</span>
                            <span className="font-bold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 px-2 py-0.5 rounded-md border border-slate-200 dark:border-slate-700 text-[11px]">
                                {ranges.length} Rango{ranges.length !== 1 ? 's' : ''} configurado{ranges.length !== 1 ? 's' : ''}
                            </span>
                            <span className="text-[11px] text-slate-400 italic hidden md:inline">
                                • 3 Sets de Iconos (Caritas, Semáforo, Flechas) y 5 Niveles de Distinción.
                            </span>
                        </div>
                        {hasEditPermission && (
                            <button
                                onClick={handleResetDefaultRanges}
                                className="text-[11px] text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 font-bold transition-colors cursor-pointer flex items-center gap-1"
                                title="Reestablecer rangos por defecto"
                            >
                                <span className="material-symbols-outlined text-[14px]">restart_alt</span>
                                Restablecer Sugeridos
                            </button>
                        )}
                    </div>
                )}

                {/* Body Content */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 bg-slate-50 dark:bg-slate-950">
                    
                    {/* --- TAB 1: EVALUACIÓN (ÁREAS Y PARÁMETROS) --- */}
                    {activeTab === 'evaluacion' && (
                        <>
                            {areas.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-64 text-center border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-6">
                                    <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-600 mb-2">fact_check</span>
                                    <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300">No hay áreas de evaluación configuradas</h4>
                                    <p className="text-xs text-slate-500 max-w-sm mt-1 mb-4">
                                        Comienza añadiendo la primera área de evaluación (ej: Prevención, Calidad, Terreno, etc.) y asigna su peso porcentual.
                                    </p>
                                    {hasEditPermission && (
                                        <button
                                            onClick={handleOpenAddArea}
                                            className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 shadow cursor-pointer"
                                        >
                                            + Añadir Primera Área
                                        </button>
                                    )}
                                </div>
                            ) : (
                                areas.map((area) => {
                                    const totalParamWeight = area.parameters.reduce((sum, p) => sum + (Number(p.weight) || 0), 0);

                                    return (
                                        <div
                                            key={area.id}
                                            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs overflow-hidden"
                                        >
                                            {/* Area Header */}
                                            <div className="p-4 bg-slate-100/70 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-wrap gap-3">
                                                <div className="flex items-center gap-3">
                                                    <span className="size-7 rounded-lg bg-blue-600 text-white font-black text-xs flex items-center justify-center shadow-xs">
                                                        N° {area.correlative}
                                                    </span>
                                                    <div>
                                                        <h4 className="text-sm font-extrabold text-slate-800 dark:text-white">
                                                            {area.name}
                                                        </h4>
                                                        <span className="text-[11px] text-slate-500 font-semibold">
                                                            Ponderador del Área: <strong className="text-blue-600 dark:text-blue-400">{area.weight}%</strong>
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                                        totalParamWeight === 100 
                                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' 
                                                            : 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                                                    }`}>
                                                        Suma Parámetros: {totalParamWeight}%
                                                    </span>

                                                    {hasEditPermission && (
                                                        <>
                                                            <button
                                                                onClick={() => handleOpenAddParam(area.id)}
                                                                className="px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 text-xs font-bold rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
                                                                title="Añadir Parámetro a esta Área"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">add</span>
                                                                Añadir Parámetro
                                                            </button>
                                                            <button
                                                                onClick={() => handleOpenEditArea(area)}
                                                                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
                                                                title="Editar Área"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">edit</span>
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteArea(area.id)}
                                                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors cursor-pointer"
                                                                title="Eliminar Área"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">delete</span>
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Parameters Table */}
                                            <div className="overflow-x-auto">
                                                {area.parameters.length === 0 ? (
                                                    <div className="p-4 text-center text-xs text-slate-400 italic">
                                                        No hay parámetros asignados a esta área. Haz clic en "Añadir Parámetro".
                                                    </div>
                                                ) : (
                                                    <table className="w-full text-left text-xs border-collapse">
                                                        <thead>
                                                            <tr className="bg-slate-50 dark:bg-slate-850 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-200 dark:border-slate-800">
                                                                <th className="px-4 py-2.5 w-12 text-center">N°</th>
                                                                <th className="px-4 py-2.5">Nombre Parámetro</th>
                                                                <th className="px-4 py-2.5 w-24 text-center">Ponderador</th>
                                                                <th className="px-4 py-2.5">Responsable (Cargo)</th>
                                                                <th className="px-4 py-2.5">Indicador</th>
                                                                <th className="px-4 py-2.5">Tipo</th>
                                                                {hasEditPermission && <th className="px-4 py-2.5 w-20 text-right">Acciones</th>}
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
                                                            {area.parameters.map(param => (
                                                                <tr key={param.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                                                    <td className="px-4 py-2.5 text-center font-bold text-slate-500">
                                                                        {area.correlative}.{param.correlative}
                                                                    </td>
                                                                    <td className="px-4 py-2.5 font-bold text-slate-800 dark:text-slate-200">
                                                                        {param.name}
                                                                    </td>
                                                                    <td className="px-4 py-2.5 text-center">
                                                                        <span className="bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 font-bold px-2 py-0.5 rounded">
                                                                            {param.weight}%
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">
                                                                        <div className="flex items-center gap-1.5">
                                                                            <span className="material-symbols-outlined text-[16px] text-slate-400">badge</span>
                                                                            <span>{param.profileName || 'Sin asignar'}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300 max-w-xs truncate" title={param.indicatorDescription}>
                                                                        {param.indicatorDescription || '-'}
                                                                    </td>
                                                                    <td className="px-4 py-2.5">
                                                                        {param.indicatorType === 'Automatico' ? (
                                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300">
                                                                                <span className="material-symbols-outlined text-[12px]">auto_awesome</span>
                                                                                {param.automaticCode}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                                                <span className="material-symbols-outlined text-[12px]">edit_note</span>
                                                                                Manual
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    {hasEditPermission && (
                                                                        <td className="px-4 py-2.5 text-right">
                                                                            <div className="flex items-center justify-end gap-1">
                                                                                <button
                                                                                    onClick={() => handleOpenEditParam(area.id, param)}
                                                                                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded transition-colors cursor-pointer"
                                                                                    title="Editar Parámetro"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[16px]">edit</span>
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => handleDeleteParam(area.id, param.id)}
                                                                                    className="p-1 text-slate-400 hover:text-red-600 rounded transition-colors cursor-pointer"
                                                                                    title="Eliminar Parámetro"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[16px]">delete</span>
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </>
                    )}

                    {/* --- TAB 2: RANGOS E INDICADORES VISUALES --- */}
                    {activeTab === 'rangos' && (
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs overflow-hidden">
                            <div className="p-4 bg-slate-100/70 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <h4 className="text-sm font-extrabold text-slate-800 dark:text-white flex items-center gap-2">
                                        <span className="material-symbols-outlined text-emerald-600 text-[20px]">palette</span>
                                        Definición de Rangos y Distinción Visual
                                    </h4>
                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                        Configura los límites numéricos de evaluación, el set de íconos (Caritas, Semáforo o Flechas) y su distinción.
                                    </p>
                                </div>
                                {hasEditPermission && (
                                    <button
                                        onClick={handleAddRange}
                                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">add</span>
                                        Añadir Rango
                                    </button>
                                )}
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50 dark:bg-slate-850 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-200 dark:border-slate-800">
                                            <th className="px-3 py-3 w-16 text-center">Orden</th>
                                            <th className="px-4 py-3 min-w-[180px]">Nombre / Etiqueta del Rango</th>
                                            <th className="px-4 py-3 min-w-[190px]">Condición / Operador</th>
                                            <th className="px-4 py-3 min-w-[200px]">Valores / Límites (%)</th>
                                            <th className="px-4 py-3 min-w-[280px]">Distinción Visual (Set e Indicador)</th>
                                            <th className="px-4 py-3 min-w-[160px] text-center">Vista Previa</th>
                                            {hasEditPermission && <th className="px-4 py-3 w-16 text-right">Acciones</th>}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
                                        {ranges.map((range, index) => {
                                            const currentLevel = range.level || 'Excelente';

                                            return (
                                                <tr key={range.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/30 transition-colors">
                                                    
                                                    {/* 1. Orden y Reordenamiento */}
                                                    <td className="px-3 py-3 text-center">
                                                        <div className="flex items-center justify-center gap-0.5">
                                                            {hasEditPermission ? (
                                                                <div className="flex flex-col">
                                                                    <button
                                                                        onClick={() => handleMoveRange(index, 'up')}
                                                                        disabled={index === 0}
                                                                        className="p-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-20 disabled:hover:text-slate-400 cursor-pointer"
                                                                        title="Subir prioridad"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[14px]">expand_less</span>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleMoveRange(index, 'down')}
                                                                        disabled={index === ranges.length - 1}
                                                                        className="p-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-20 disabled:hover:text-slate-400 cursor-pointer"
                                                                        title="Bajar prioridad"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[14px]">expand_more</span>
                                                                    </button>
                                                                </div>
                                                            ) : null}
                                                            <span className="size-5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-bold text-[10px] flex items-center justify-center">
                                                                #{index + 1}
                                                            </span>
                                                        </div>
                                                    </td>

                                                    {/* 2. Nombre / Etiqueta */}
                                                    <td className="px-4 py-3">
                                                        {hasEditPermission ? (
                                                            <input
                                                                type="text"
                                                                value={range.name}
                                                                onChange={e => handleUpdateRange(range.id, { name: e.target.value })}
                                                                placeholder="Ej: Sobresaliente, Aceptable, Deficiente..."
                                                                className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-bold text-xs outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                                                            />
                                                        ) : (
                                                            <span className="font-bold text-slate-800 dark:text-white">{range.name}</span>
                                                        )}
                                                    </td>

                                                    {/* 3. Condición / Operador */}
                                                    <td className="px-4 py-3">
                                                        {hasEditPermission ? (
                                                            <select
                                                                value={range.operator}
                                                                onChange={e => handleUpdateRange(range.id, { operator: e.target.value as EvaluationRangeOperator })}
                                                                className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-semibold text-xs outline-none focus:ring-2 focus:ring-emerald-500 transition-all cursor-pointer"
                                                            >
                                                                {RANGE_OPERATOR_OPTIONS.map(op => (
                                                                    <option key={op.value} value={op.value}>{op.label}</option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <span className="text-slate-700 dark:text-slate-300 font-medium">
                                                                {RANGE_OPERATOR_OPTIONS.find(op => op.value === range.operator)?.label || range.operator}
                                                            </span>
                                                        )}
                                                    </td>

                                                    {/* 4. Valores / Límites Dinámicos */}
                                                    <td className="px-4 py-3">
                                                        {range.operator === 'between' ? (
                                                            <div className="flex items-center gap-1.5">
                                                                <div className="flex items-center gap-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1">
                                                                    <span className="text-[10px] text-slate-400 font-bold uppercase">De:</span>
                                                                    <input
                                                                        type="number"
                                                                        value={range.minValue !== undefined ? range.minValue : 0}
                                                                        onChange={e => handleUpdateRange(range.id, { minValue: parseFloat(e.target.value) || 0 })}
                                                                        disabled={!hasEditPermission}
                                                                        className="w-12 bg-transparent text-slate-800 dark:text-white font-bold text-xs outline-none text-right"
                                                                    />
                                                                    <span className="text-[10px] text-slate-400">%</span>
                                                                </div>
                                                                <span className="text-slate-400 text-xs font-bold">a</span>
                                                                <div className="flex items-center gap-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1">
                                                                    <span className="text-[10px] text-slate-400 font-bold uppercase">Hasta:</span>
                                                                    <input
                                                                        type="number"
                                                                        value={range.maxValue !== undefined ? range.maxValue : 100}
                                                                        onChange={e => handleUpdateRange(range.id, { maxValue: parseFloat(e.target.value) || 0 })}
                                                                        disabled={!hasEditPermission}
                                                                        className="w-12 bg-transparent text-slate-800 dark:text-white font-bold text-xs outline-none text-right"
                                                                    />
                                                                    <span className="text-[10px] text-slate-400">%</span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1.5">
                                                                <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1 max-w-[140px]">
                                                                    <span className="text-xs text-slate-500 font-bold">
                                                                        {RANGE_OPERATOR_OPTIONS.find(op => op.value === range.operator)?.symbol || range.operator}
                                                                    </span>
                                                                    <input
                                                                        type="number"
                                                                        value={range.value !== undefined ? range.value : 0}
                                                                        onChange={e => handleUpdateRange(range.id, { value: parseFloat(e.target.value) || 0 })}
                                                                        disabled={!hasEditPermission}
                                                                        className="w-16 bg-transparent text-slate-800 dark:text-white font-bold text-xs outline-none text-right"
                                                                    />
                                                                    <span className="text-[10px] text-slate-400 font-bold">%</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>

                                                    {/* 5. Distinción Visual: 3 Sets (Caritas, Semáforo, Flechas) y 5 Niveles */}
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            {/* Selector de Set Visual (Solo 3 opciones) */}
                                                            <select
                                                                value={range.visualType}
                                                                onChange={e => handleUpdateRange(range.id, { visualType: e.target.value as VisualDistinctionType })}
                                                                disabled={!hasEditPermission}
                                                                className="w-28 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-bold text-[11px] outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                                                            >
                                                                {VISUAL_SETS.map(s => (
                                                                    <option key={s.type} value={s.type}>{s.label}</option>
                                                                ))}
                                                            </select>

                                                            {/* Selector de Distinción (5 opciones estándar) */}
                                                            <select
                                                                value={currentLevel}
                                                                onChange={e => handleUpdateRange(range.id, { level: e.target.value as VisualDistinctionLevel })}
                                                                disabled={!hasEditPermission}
                                                                className="flex-1 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-semibold text-[11px] outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                                                            >
                                                                {DISTINCTION_LEVELS.map(d => (
                                                                    <option key={d.level} value={d.level}>
                                                                        {d.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </td>

                                                    {/* 6. Vista Previa: Ícono con su Distinción */}
                                                    <td className="px-4 py-3 text-center">
                                                        <EvaluationRangeBadge
                                                            range={range}
                                                            showDistinction={true}
                                                            showScore={false}
                                                        />
                                                    </td>

                                                    {/* 7. Acciones */}
                                                    {hasEditPermission && (
                                                        <td className="px-4 py-3 text-right">
                                                            <button
                                                                onClick={() => handleDeleteRange(range.id)}
                                                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors cursor-pointer"
                                                                title="Eliminar este Rango"
                                                            >
                                                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
                    >
                        Cerrar
                    </button>
                    {hasEditPermission && (
                        <button
                            onClick={handleSaveGlobalConfig}
                            className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
                        >
                            <span className="material-symbols-outlined text-[18px]">save</span>
                            Guardar Configuración
                        </button>
                    )}
                </div>
            </div>

            {/* Modal: Añadir/Editar Área */}
            {areaModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-md p-5 flex flex-col gap-4">
                        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                            <h4 className="text-sm font-extrabold text-slate-800 dark:text-white uppercase flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-600">category</span>
                                {editingAreaId ? 'Editar Área de Evaluación' : 'Añadir Área de Evaluación'}
                            </h4>
                            <button onClick={() => setAreaModalOpen(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>

                        <div className="space-y-3 text-xs">
                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                                    Nombre de Área *
                                </label>
                                <input
                                    type="text"
                                    placeholder="Ej: Calidad, Prevención de Riesgos, Terreno y Operación..."
                                    value={areaForm.name}
                                    onChange={e => setAreaForm({ ...areaForm, name: e.target.value })}
                                    className="w-full border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-medium outline-none focus:ring-2 focus:ring-blue-500"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                                    Ponderador (Peso) de Área (%) *
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    placeholder="Ej: 25"
                                    value={areaForm.weight}
                                    onChange={e => setAreaForm({ ...areaForm, weight: e.target.value })}
                                    className="w-full border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-bold outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
                            <button
                                onClick={() => setAreaModalOpen(false)}
                                className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 rounded-xl cursor-pointer"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveArea}
                                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow transition-all cursor-pointer"
                            >
                                {editingAreaId ? 'Actualizar Área' : 'Guardar Área'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Añadir/Editar Parámetro */}
            {paramModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-lg p-5 flex flex-col gap-4">
                        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                            <h4 className="text-sm font-extrabold text-slate-800 dark:text-white uppercase flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-600">checklist</span>
                                {editingParamId ? 'Editar Parámetro' : 'Añadir Parámetro'}
                            </h4>
                            <button onClick={() => setParamModalOpen(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>

                        <div className="space-y-3 text-xs max-h-[70vh] overflow-y-auto pr-1">
                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                                    Nombre del Parámetro *
                                </label>
                                <input
                                    type="text"
                                    placeholder="Ej: Cumplimiento de Programa Semanal, Charla de Seguridad..."
                                    value={paramForm.name}
                                    onChange={e => setParamForm({ ...paramForm, name: e.target.value })}
                                    className="w-full border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-medium outline-none focus:ring-2 focus:ring-blue-500"
                                    autoFocus
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                                        Ponderador (%) *
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="100"
                                        placeholder="Ej: 50"
                                        value={paramForm.weight}
                                        onChange={e => setParamForm({ ...paramForm, weight: e.target.value })}
                                        className="w-full border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-bold outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                                        Responsable (Perfil de Cargo)
                                    </label>
                                    <select
                                        value={paramForm.profileId}
                                        onChange={e => setParamForm({ ...paramForm, profileId: e.target.value })}
                                        className="w-full border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-medium outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Seleccione perfil...</option>
                                        {globalProfiles.filter(p => !p.isDeleted).map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                                    Indicador (Descripción / Criterio de Medición)
                                </label>
                                <textarea
                                    rows={2}
                                    placeholder="Describe qué se evaluará o la métrica a considerar..."
                                    value={paramForm.indicatorDescription}
                                    onChange={e => setParamForm({ ...paramForm, indicatorDescription: e.target.value })}
                                    className="w-full border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-medium outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                                    Tipo de Indicador *
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setParamForm({ ...paramForm, indicatorType: 'Manual' })}
                                        className={`p-2.5 rounded-xl border flex items-center justify-center gap-2 font-bold text-xs transition-all cursor-pointer ${
                                            paramForm.indicatorType === 'Manual'
                                                ? 'bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-500 shadow-sm'
                                                : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50'
                                        }`}
                                    >
                                        <span className="material-symbols-outlined text-[18px]">edit_note</span>
                                        Manual
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setParamForm({ ...paramForm, indicatorType: 'Automatico' })}
                                        className={`p-2.5 rounded-xl border flex items-center justify-center gap-2 font-bold text-xs transition-all cursor-pointer ${
                                            paramForm.indicatorType === 'Automatico'
                                                ? 'bg-purple-50 border-purple-500 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-500 shadow-sm'
                                                : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50'
                                        }`}
                                    >
                                        <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
                                        Automático
                                    </button>
                                </div>
                            </div>

                            {/* Detalle para Automático */}
                            {paramForm.indicatorType === 'Automatico' && (
                                <div className="p-3 bg-purple-50/60 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/40 rounded-xl space-y-2">
                                    <label className="block text-[11px] font-black text-purple-900 dark:text-purple-300 uppercase">
                                        Selecciona la Métrica Automática de la Plataforma:
                                    </label>
                                    <div className="space-y-1.5">
                                        {AUTOMATIC_INDICATOR_OPTIONS.map(opt => (
                                            <label
                                                key={opt.code}
                                                className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                                                    paramForm.automaticCode === opt.code
                                                        ? 'bg-white dark:bg-slate-800 border-purple-500 shadow-sm text-purple-900 dark:text-purple-200'
                                                        : 'border-transparent text-slate-600 dark:text-slate-400 hover:bg-white/50 dark:hover:bg-slate-800/50'
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="autoMetric"
                                                    checked={paramForm.automaticCode === opt.code}
                                                    onChange={() => setParamForm({ ...paramForm, automaticCode: opt.code })}
                                                    className="mt-0.5 text-purple-600 focus:ring-purple-500"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-bold text-xs">{opt.label}</div>
                                                    <div className="text-[10px] text-slate-500 dark:text-slate-400">{opt.desc}</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
                            <button
                                onClick={() => setParamModalOpen(false)}
                                className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 rounded-xl cursor-pointer"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveParam}
                                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow transition-all cursor-pointer"
                            >
                                {editingParamId ? 'Actualizar Parámetro' : 'Guardar Parámetro'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
